import { Task } from '@lit/task';
import { html, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { Card } from '~/modules/graphql/generated/graphql';
import { graphql } from '~/modules/graphql/generated';

import contentfulService from '~/modules/services/contentful-graphql-service';
import DERModuleElement from '~/modules/utils/der-module-element';

import '~/ui/components/button/';
import '~/ui/components/content-icon';
import '~/ui/components/module-title';
import '~/ui/objects/module/body';
import '~/ui/objects/module/module';
import '~/ui/objects/module/optional';
import '~/ui/objects/module/title';

import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from './der-tailormade-usp-box.styles';

export class UspBoxComponent extends DERModuleElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @property({ type: String, attribute: 'button-text' })
    buttonText: string = 'Mehr erfahren';

    @property({ type: String, attribute: 'link-url' })
    linkUrl: string = '';

    suptitle: string = 'Ihre Vorteile';

    override title: string = 'Title';

    intro: string = 'Text';

    cardsCollection: Card[] = [];

    entryId: string = '5wCkj3DjZRfLapesDtxvGv';

    /**
     * GraphQL query for fetching USP box content from Contentful.
     * Retrieves localized content including title, intro, cards, and button text.
     *
     * @type {object}
     * @readonly
     * @private
     */
    UspBoxQuery = graphql(`
        query UspBox($entryId: String!, $locale: String!) {
            uspBox(id: $entryId, locale: $locale) {
                suptitle
                title
                intro {
                    json
                }
                buttonText
                cardsCollection {
                    items {
                        title
                        text {
                            json
                        }
                        icon {
                            url
                        }
                    }
                }
            }
        }
    `);

    /**
     * Task instance that manages asynchronous data fetching from Contentful.
     * Handles pending, complete, and error states. Re-executes when language changes.
     *
     * @type {Task<any>}
     * @private
     *
     * @fires 'Task#pending' - When data is being fetched
     * @fires 'Task#complete' - When data is successfully loaded
     * @fires 'Task#error' - When data fetch fails
     */
    private _contentTask = new Task(this, {
        task: async () => {
            const response = await contentfulService.fetchGraphQL(this.UspBoxQuery, {
                entryId: this.entryId,
                locale: this.lang,
            });

            if (!response) {
                throw new Error('Error fetching content with entryId: ' + this.entryId + ' and lang: ' + this.lang);
            }
            this.title = <string>response.uspBox?.title;
            this.suptitle = <string>response.uspBox?.suptitle;
            if (response.uspBox?.buttonText != null) {
                this.buttonText = <string>response.uspBox?.buttonText;
            }
            this.intro = <string>response.uspBox?.intro?.json?.content[0].content[0].value;
            this.cardsCollection = <Card[]>response.uspBox?.cardsCollection?.items;
        },
        args: () => [this.lang],
    });

    /**
     * Renders the component template based on task state.
     *
     * Renders different templates based on the async task state:
     * - pending: Empty template (loading state)
     * - complete: Full module layout with cards and optional button
     * - error: Empty template with logged warning
     *
     * The complete state renders:
     * - Module wrapper with title section
     * - Optional secondary button (if linkUrl is provided)
     * - Card list with icons and descriptions
     *
     * @override
     * @returns '{TemplateResult}' Lit template result
     */
    override render() {
        return this._contentTask.render({
            pending: () => html``,
            complete: () => html`
                <der-ui-module-wrapper>
                    <div class="m-usp-product">
                        <der-ui-title-wrapper>
                            <der-ui-module-title
                                suptitle="${this.suptitle}"
                                title="${this.title}"
                                intro="${this.intro}"
                                module-name="m-usp"
                            ></der-ui-module-title>
                        </der-ui-title-wrapper>

                        ${this.linkUrl !== ''
                            ? html`
                                  <der-ui-optional-wrapper>
                                      <der-ui-button
                                          variant="secondary"
                                          label="${this.buttonText}"
                                          url="${this.linkUrl}"
                                      ></der-ui-button>
                                  </der-ui-optional-wrapper>
                              `
                            : ''}

                        <der-ui-body-wrapper>
                            <div class="m-usp-list">
                                ${this.cardsCollection.map(
                                    (item) => html`
                                        <div class="m-usp-list__item">
                                            ${item.icon?.url
                                                ? html` <div class="m-usp-list__item-icon-wrapper">
                                                      <der-ui-content-icon iconUrl=${item.icon.url}
                                                          >${item.icon.url}
                                                      </der-ui-content-icon>
                                                  </div>`
                                                : null}
                                            <div class="m-usp-list__item-text-wrapper">
                                                <h3 class="m-usp-list__item-title">${item.title}</h3>
                                                <p class="m-usp-list__item-text">
                                                    ${item.text?.json?.content[0].content[0].value}
                                                </p>
                                            </div>
                                        </div>
                                    `
                                )}
                            </div>
                        </der-ui-body-wrapper>
                    </div>
                </der-ui-module-wrapper>
            `,
            error: () => {
                this.warn(this.tagName, {
                    message: 'Can not load data with entryId: ' + this.entryId + ' and lang: ' + this.lang,
                });
                return html``;
            },
        });
    }
}
